import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, ComplexValue } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { replaceTemplateString } from 'app/infrastructure/platform/text';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';

import { textConfigurationTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestText, ReceiveText, FailText,
  RequestDeleteText, ReceiveDeleteText, FailDeleteText,
  RequestSaveText, ReceiveSaveText, FailSaveText,
  ClearForm,
  UpdateTextSet, UpdateTextCollection,
  ChangeText, CopyTextToAll,
  UpdateValidation
} from './action-types';
import { api } from './api';
import { mapTextItemFromWire, mapTextItemToWire } from './map-text-item';
import { TextConfigurationItem, TextItemValidation } from './types-store';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();
        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  loadText: (textId: number, textSet: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestText(textId, textSet));

      try {

        const textItem = await api.loadText(textId, textSet);
        dispatch(new ReceiveText(textId, textSet, mapTextItemFromWire(textItem)));
      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.textConfiguration,
            translationId: textConfigurationTranslation.no_access_to_text_configuration
          });
        } else {
          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: routeConstants.textConfigurationList });
        }
        dispatch(new FailText(textId, textSet));
      }
    },

  deleteText: (textId: number, textSet: number, successMessage: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestDeleteText(textId, textSet));

      try {
        await api.deleteText(textId, textSet);

        navigate.to({ pathname: routeConstants.textConfigurationList, state: { internal: true } });

        dispatch(new ReceiveDeleteText(textId, textSet));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteText(textId, textSet));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: routeConstants.textConfigurationList, state: { internal: true } });
        }
      }
    },

  saveText: (
    text: TextConfigurationItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<any, ApplicationState> =>
    async (dispatch: any, getState: any) => {

      const textId = text?.id;
      const textSet = text?.textSet?.value;

      if (!isDefinedIncludingZero(textSet)) {
        return;
      }

      dispatch(new RequestSaveText(textId, textSet));

      try {
        const updatedTextId = isDefinedIncludingZero(textId)
          ? await api.saveText(textId, textSet, mapTextItemToWire(text))
          : await api.createText(mapTextItemToWire(text));

        dispatch(new ReceiveSaveText(textId, textSet));

        navigate.to({ pathname: routeConstants.textConfigurationList, state: { internal: true } });

        dispatch(new SuccessNotification(replaceTemplateString(
          successMessage, updatedTextId, textSet
        )));
      } catch (err) {

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        dispatch(new FailSaveText(textId, textSet));

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: routeConstants.textConfigurationList, state: { internal: true } });
        }
      }
    },

  clearForm: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ClearForm());
    },

  onTextSetChange: (textSet: ComplexValue<number>): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateTextSet(textSet));
    },

  onTextCollectionChange: (textCollection: ComplexValue<number>[]): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateTextCollection(textCollection));
    },

  onTextChange: (languageId: number, text: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeText(languageId, text));
    },

  copyTextToAllTranslations: (languageId: number, successMessage: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new CopyTextToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: TextItemValidation): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateValidation(validation));
    },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
